<ng-template let-extraClass="extraClass" #topNav>
  @if (!isRightSideContent) {
    <tidy-top-nav
      [class]="extraClass"
      [title]="'Edit Guidebook Content'"
      [canGoBack]="true" />
  }
</ng-template>

<ng-container
  *ngTemplateOutlet="topNav;context:{ extraClass: 'mobile' }" />

<ng-template #content>
  <tidy-wrapper [class]="isRightSideContent ? 'right-side-scroll-enabled' : ''">
    @if (errorMessage) {
      <tidy-error-message
        [text]="errorMessage" />
    }

    @if (!isDataLoaded) {
      <div style="width: 100%">
        <ion-skeleton-text
          animated="true"
          style="height: 75px; border-radius: 6px" />
      </div>
      <div style="width: 100%; padding-top: 1rem;">
        <ion-skeleton-text
          animated="true"
          style="height: 200px; border-radius: 6px" />
      </div>
    }

    @if (isDataLoaded && addressId) {
      <tidy-row>
        <tidy-text
          [header]="propertyName"
          [translate]="false" />
      </tidy-row>

      <tidy-row>
        <tidy-text
          [helper]="'Edit the content guests see in the digital guidebook for this property. Drag categories to reorder how guests see them.'" />
      </tidy-row>

      <!-- Preview -->
      <tidy-card>
        <tidy-row>
          <tidy-image
            [src]="'assets/svg/eye-outline.svg'"
            class="body-size" />
          <tidy-text
            [header]="'Guest Preview'" />
        </tidy-row>

        @if (previewUrl) {
          <tidy-row>
            @if (isDemoPreview) {
              <tidy-text
                [helper]="'This is a demo preview with sample data. Save your changes to refresh.'" />
            } @else {
              <tidy-text
                [helper]="'This is what guests see. Save your changes to refresh the preview.'" />
            }
          </tidy-row>
          <iframe
            [src]="safePreviewUrl"
            title="Guidebook preview"
            loading="lazy"
            style="width: 100%; height: 300px; margin-top: 10px; border: 1px solid #e5e7eb; border-radius: 12px; background: #f9fafb;"></iframe>
          <tidy-row>
            <tidy-button
              [text]="'Open Live Preview'"
              [action]="boundOpenPreview"
              class="secondary" />
          </tidy-row>
        }
        @if (!previewUrl) {
          <tidy-row>
            <tidy-text
              [helper]="'A live preview will appear once a reservation is created. Use the demo preview to see how the guidebook looks with sample data.'" />
          </tidy-row>
          <tidy-row>
            <tidy-button
              [text]="'Show Demo Preview'"
              [action]="boundOpenDemoPreview"
              class="secondary" />
          </tidy-row>
        }
      </tidy-card>

      <form [formGroup]="form">
        <!-- Welcome Message -->
        <tidy-card>
          <tidy-row>
            <tidy-image
              [src]="'assets/svg/chatbubble-outline.svg'"
              class="body-size" />
            <tidy-text
              [header]="'Welcome Message'" />
          </tidy-row>
          <tidy-row>
            <tidy-textarea
              [label]="'Welcome Message'"
              formControlName="welcome_message"
              [form]="form.controls.welcome_message"
              [icon]="'assets/svg/create-outline.svg'" />
          </tidy-row>
          <tidy-row [alignLastItemRight]="true">
            <tidy-text [body]="'Visible to guest'" />
            <tidy-toggle
              [toggled]="form.controls.welcome_message_visible.value"
              (toggleChanged)="form.controls.welcome_message_visible.setValue(!form.controls.welcome_message_visible.value)" />
          </tidy-row>
        </tidy-card>

        <!-- Check-In Instructions -->
        <tidy-card>
          <tidy-row>
            <tidy-image
              [src]="'assets/svg/log-in-outline.svg'"
              class="body-size" />
            <tidy-text
              [header]="'Check-In Instructions'" />
          </tidy-row>
          <tidy-row>
            <tidy-textarea
              [label]="'Check-In Instructions'"
              formControlName="check_in_instructions"
              [form]="form.controls.check_in_instructions"
              [icon]="'assets/svg/create-outline.svg'" />
          </tidy-row>
          <tidy-row [alignLastItemRight]="true">
            <tidy-text [body]="'Visible to guest'" />
            <tidy-toggle
              [toggled]="form.controls.check_in_instructions_visible.value"
              (toggleChanged)="form.controls.check_in_instructions_visible.setValue(!form.controls.check_in_instructions_visible.value)" />
          </tidy-row>
        </tidy-card>

        <!-- House Rules -->
        <tidy-card>
          <tidy-row>
            <tidy-image
              [src]="'assets/svg/document-text-outline.svg'"
              class="body-size" />
            <tidy-text
              [header]="'House Rules'" />
          </tidy-row>
          <tidy-row>
            <tidy-textarea
              [label]="'House Rules'"
              formControlName="house_rules"
              [form]="form.controls.house_rules"
              [icon]="'assets/svg/create-outline.svg'" />
          </tidy-row>
          <tidy-row [alignLastItemRight]="true">
            <tidy-text [body]="'Visible to guest'" />
            <tidy-toggle
              [toggled]="form.controls.house_rules_visible.value"
              (toggleChanged)="form.controls.house_rules_visible.setValue(!form.controls.house_rules_visible.value)" />
          </tidy-row>
        </tidy-card>

        <!-- Category groups (draggable to reorder) -->
        <div formGroupName="fields">
          <tidy-drag-drop
            dragDropId="guidebook-categories-dd"
            [dataList]="categories"
            [boundary]="'tidy-wrapper'"
            (listDropped)="onSectionsReordered($event)">
            @for (category of categories; track category.id) {
              <ng-container *tidyDragDropItem>
                <tidy-card>
                  <tidy-row
                    [alignLastItemRight]="true"
                    class="vertical-align-center"
                    (click)="toggleCategory(category.id)"
                    style="cursor: pointer;">
                    <tidy-image
                      [src]="'assets/svg/menu-outline.svg'"
                      class="title-size drag-handle-icon" />
                    <tidy-image
                      [src]="category.icon"
                      class="body-size" />
                    <tidy-text
                      [header]="category.label" />
                    <tidy-image
                      [src]="collapsedCategories[category.id] ? 'assets/svg/chevron-down-outline.svg' : 'assets/svg/chevron-up-outline.svg'"
                      class="body-size" />
                  </tidy-row>

                  @if (!collapsedCategories[category.id]) {
                    @if (category.id === 'contacts') {
                      <tidy-row>
                        <tidy-text
                          [helper]="'Guests will see a Contact TIDY button with our support phone number. This is provided automatically and cannot be edited.'" />
                      </tidy-row>
                    }

                    @for (field of category.fields; track field.key) {
                      @if (field.inputType !== 'select') {
                        <tidy-row>
                          @if (field.inputType === 'textarea') {
                            <tidy-textarea
                              [label]="field.label"
                              [formControl]="fieldControls[field.key]"
                              [icon]="'assets/svg/create-outline.svg'" />
                          } @else {
                            <tidy-input
                              [label]="field.label"
                              [formControl]="fieldControls[field.key]"
                              [icon]="'assets/svg/create-outline.svg'" />
                          }
                          @if (field.fromAddressModel) {
                            <tidy-text
                              [helper]="'Synced from property setup — edits here update the property record.'" />
                          }
                        </tidy-row>
                      }
                    }

                    @if (category.fields.length > 0 && !category.fixed) {
                      <tidy-row [alignLastItemRight]="true">
                        <tidy-text [body]="'Visible to guest'" />
                        <tidy-toggle
                          [toggled]="categoryVisibilityControls[category.id].value"
                          (toggleChanged)="categoryVisibilityControls[category.id].setValue(!categoryVisibilityControls[category.id].value)" />
                      </tidy-row>
                    }
                  }
                </tidy-card>
              </ng-container>
            }
          </tidy-drag-drop>
        </div>

        <!-- Guest Settings -->
        <tidy-card>
          <tidy-row>
            <tidy-image
              [src]="'assets/svg/settings-outline.svg'"
              class="body-size" />
            <tidy-text
              [header]="'Guest Settings'" />
          </tidy-row>
          <tidy-row [alignLastItemRight]="true">
            <tidy-text
              [body]="'Collect guest contact info'" />
            <tidy-toggle
              [toggled]="form.controls.contact_collection_enabled.value"
              (toggleChanged)="form.controls.contact_collection_enabled.setValue(!form.controls.contact_collection_enabled.value)" />
          </tidy-row>
          <tidy-row [alignLastItemRight]="true">
            <tidy-text
              [body]="'Require identity verification'" />
            <tidy-toggle
              [toggled]="form.controls.identity_verification_enabled.value"
              (toggleChanged)="form.controls.identity_verification_enabled.setValue(!form.controls.identity_verification_enabled.value)" />
          </tidy-row>
        </tidy-card>

        <!-- Custom Sections -->
        <tidy-card>
          <tidy-row>
            <tidy-image
              [src]="'assets/svg/add-circle-outline.svg'"
              class="body-size" />
            <tidy-text
              [header]="'Custom Sections'" />
          </tidy-row>
          <tidy-row>
            <tidy-text
              [helper]="'Add your own sections (e.g. Pool Code, Trash Schedule). Guests will see them in the guidebook.'" />
          </tidy-row>

          <div formArrayName="custom_sections">
            @for (section of customSections.controls; track $index) {
              @if (!section.get('deleted')?.value) {
                <div [formGroupName]="$index" class="field-top-padding">
                  <tidy-card>
                    <tidy-row>
                      <tidy-input
                        [label]="'Section Title'"
                        formControlName="label"
                        [form]="section.get('label')"
                        [icon]="'assets/svg/pricetag-outline.svg'" />
                    </tidy-row>
                    <tidy-row>
                      <tidy-textarea
                        [label]="'Content'"
                        formControlName="value"
                        [form]="section.get('value')"
                        [icon]="'assets/svg/create-outline.svg'" />
                    </tidy-row>
                    <tidy-row [alignLastItemRight]="true">
                      <tidy-text [body]="'Visible to guest'" />
                      <tidy-toggle
                        [toggled]="section.get('visible')?.value"
                        (toggleChanged)="section.get('visible')?.setValue(!section.get('visible')?.value)" />
                    </tidy-row>
                    <tidy-row>
                      <tidy-text
                        [actionBody]="'Remove Section'"
                        (action)="removeCustomSection($index)"
                        class="delete-automation-link" />
                    </tidy-row>
                  </tidy-card>
                </div>
              }
            }
          </div>

          <tidy-row>
            <tidy-button
              [text]="'Add Section'"
              [action]="boundAddCustomSection"
              class="secondary" />
          </tidy-row>
        </tidy-card>

        <!-- Save Button -->
        <tidy-row>
          <tidy-button
            [text]="'Save Changes'"
            [action]="boundSave"
            class="primary" />
        </tidy-row>
      </form>
    }
  </tidy-wrapper>
</ng-template>

@if (!isRightSideContent) {
  <app-scroll-wrapper [templateContent]="content" />
}

@if (isRightSideContent) {
  <ion-content>
    <ng-container *ngTemplateOutlet="content" />
  </ion-content>
}
